/**
 * A watched subject's checks as timeline rows: quiet stretches folded to one line.
 *
 * Pure, so CRA's jest can test it.
 */
import type { WatchTimelinePoint } from '../../types';

export type TimelineRow =
  | { type: 'point'; point: WatchTimelinePoint }
  | { type: 'quiet'; from: string | null; to: string | null; count: number; reading: string | null; key: string };

/** Points newest first into rows: each run of quiet checks is one row. Pure. */
export function foldQuiet(points: WatchTimelinePoint[]): TimelineRow[] {
  const out: TimelineRow[] = [];
  let run: WatchTimelinePoint[] = [];
  const flush = () => {
    if (!run.length) return;
    // Newest first: the run's first point is its end, its last its start.
    out.push({
      type: 'quiet',
      from: run[run.length - 1].at,
      to: run[0].at,
      count: run.length,
      reading: run[0].reading,
      key: `quiet-${run[0].run_id}`,
    });
    run = [];
  };
  for (const p of points) {
    if (p.kind === 'quiet' && !p.partial.length) {
      run.push(p);
      continue;
    }
    flush();
    out.push({ type: 'point', point: p });
  }
  flush();
  return out;
}

/** "+160", "-57", or nothing. Pure. */
export function deltaWords(delta: number | null): string {
  if (!delta) return '';
  return delta > 0 ? `+${delta}` : `${delta}`;
}

/** "7 × new section · 1 × publishing pace", the alerts a check raised, by kind. Pure. */
export function changeSummary(events: { title: string }[]): string {
  const counts = new Map<string, number>();
  for (const e of events) counts.set(e.title, (counts.get(e.title) ?? 0) + 1);
  return Array.from(counts.entries())
    .sort((a, b) => b[1] - a[1])
    .map(([title, n]) => (n > 1 ? `${n} × ${title.toLowerCase()}` : title.toLowerCase()))
    .join(' · ');
}
